/**
 * SheetChartPopover — renders a native Excel chart (parsed from the .xlsx by
 * {@link parseXlsxCharts}) with the shared ECharts `<ChartRaw>` wrapper, inside
 * a Univer canvas-popup anchored on the sheet.
 *
 * Why (Jun 24, 2026, Fabio): Univer's OSS core preset has no chart plugin, so
 * imported native charts vanish in the in-app editor (they're fine in the
 * downloaded .xlsx). We re-draw them from their cached data here so the user
 * sees the chart in-app too. View-only — the canonical chart still lives in the
 * .xlsx and round-trips untouched.
 *
 * Registered as a Univer component; receives its {@link ParsedSheetChart} via
 * ``attachPopup`` ``extraProps`` (same mechanism as the AI cell popover).
 */

import type { EChartsOption } from "echarts";
import * as React from "react";
import { ANDO_CHART_COLORS, ChartRaw } from "../charts";
import type { ParsedSheetChart } from "./xlsxChartParse";

export const SHEET_CHART_KEY = "AndoSheetChart";

export interface SheetChartPopoverProps {
  readonly spec: ParsedSheetChart;
}

/** Map a parsed xlsx chart onto an on-brand ECharts option. */
export function buildXlsxChartOption(spec: ParsedSheetChart): EChartsOption {
  const base: EChartsOption = {
    color: [...ANDO_CHART_COLORS],
    title: spec.title
      ? { text: spec.title, left: "center", textStyle: { fontSize: 13, fontWeight: 600, color: "#2B2A28" } }
      : undefined,
    tooltip: { trigger: spec.type === "pie" ? "item" : "axis" },
    legend:
      spec.series.length > 1 || spec.type === "pie"
        ? { bottom: 0, type: "scroll", textStyle: { fontSize: 11 } }
        : undefined,
    grid: { left: 8, right: 16, top: spec.title ? 36 : 16, bottom: 28, containLabel: true },
  };

  if (spec.type === "pie") {
    const values = spec.series[0]?.values ?? [];
    return {
      ...base,
      series: [
        {
          type: "pie",
          radius: ["35%", "68%"],
          center: ["50%", "50%"],
          data: spec.categories.map((name, i) => ({ name, value: values[i] ?? 0 })),
          label: { fontSize: 11 },
        },
      ],
    };
  }

  const isArea = spec.type === "area";
  const isLine = spec.type === "line" || isArea;
  return {
    ...base,
    xAxis: {
      type: "category",
      data: spec.categories,
      axisLabel: { fontSize: 11, color: "#56544F" },
    },
    yAxis: { type: "value", axisLabel: { fontSize: 11, color: "#56544F" } },
    series: spec.series.map((s) => ({
      name: s.name,
      type: isLine ? "line" : "bar",
      smooth: isLine,
      areaStyle: isArea ? {} : undefined,
      data: s.values,
    })),
  };
}

/**
 * Univer canvas-popup content. Fixed frame so the chart floats over the sheet
 * at its anchor cell like a real embedded chart. White card + subtle shadow to
 * read as a distinct object above the grid.
 */
export const SheetChartPopover: React.FC<SheetChartPopoverProps> = ({ spec }) => {
  // Hooks must run unconditionally (rules-of-hooks): compute before any return.
  const option = React.useMemo(
    () => (spec?.series?.length ? buildXlsxChartOption(spec) : null),
    [spec],
  );
  if (!option) return null;
  return (
    <div
      style={{
        width: 480,
        height: 300,
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 8,
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        padding: 8,
        boxSizing: "border-box",
      }}
    >
      <ChartRaw option={option} />
    </div>
  );
};

SheetChartPopover.displayName = "SheetChartPopover";
